import type { AnnouncementWidget } from './AnnouncementWidget';
import {
    TimelineSlot,
    WidgetType,
    ZodBaseWidgetConfiguration,
    ZodGenericWidgetConfigurationDetails,
} from '../types';
import { z } from 'zod';
import type { Announcement } from './Announcement';
import {
    zMarshalToBoolean,
    zMarshalToNumber,
    zMarshalToNumberNullable,
    zMarshalToStrictInt,
    zMarshalToStrictIntNullable,
} from '../../ReachApi/Marshallers';

export const DEFAULT_BACKGROUND_COLOR = '' as const;

export const SM_FACEBOOK = 'facebook' as const;
export const SM_INSTAGRAM = 'instagram' as const;
export const SM_TWITTER = 'twitter' as const;

export type AnnouncementTimelineSlot = TimelineSlot<Announcement, AnnouncementWidget>;
export type UnknownTimelineSlot = TimelineSlot;

export type AnnouncementWidgetTimelineSlotParams = Announcement;

export type AnnouncementWidgetTimelineSlot = TimelineSlot<
    AnnouncementWidgetTimelineSlotParams,
    AnnouncementWidget
>;

export const ZodAnnouncementWidgetConfigurationDetails =
    ZodGenericWidgetConfigurationDetails.extend({
        facilityid: z.string(),
        screenid: z.string(),
        background_color: z.string().nullish(),
        shuffle: z.unknown().transform(zMarshalToBoolean),
        mute_videos: z.unknown().transform(zMarshalToBoolean),
        is_new_media_library: z.unknown().transform(zMarshalToBoolean),
        filter_expression: z.string().nullish(),
        transform_method: z.string().nullish(),
    });

export type AnnouncementWidgetConfigurationDetails = z.infer<
    typeof ZodAnnouncementWidgetConfigurationDetails
>;

const ZodAnnouncementWidgetConfiguration = ZodBaseWidgetConfiguration.extend({
    type: z.literal(WidgetType.ANNOUNCEMENT),
    config: ZodAnnouncementWidgetConfigurationDetails,
});

export type AnnouncementWidgetConfiguration = z.infer<typeof ZodAnnouncementWidgetConfiguration>;

export enum AnnouncementState {
    PROVISIONED = 'provisioned',
    UNPROVISIONED = 'unprovisioned',
}

export enum AnnouncementStatus {
    ACTIVE = 'active',
    APPROVED = 'approved',
    DELETED = 'deleted',
    DRAFT = 'draft',
    EXPIRED = 'expired',
    INACTIVE = 'inactive',
    NEED_APPROVAL = 'needapp',
}

export enum AbbreviatedDaysOfWeek {
    SUNDAY = 'Sun',
    MONDAY = 'Mon',
    TUESDAY = 'Tue',
    WEDNESDAY = 'Wed',
    THURSDAY = 'Thu',
    FRIDAY = 'Fri',
    SATURDAY = 'Sat',
}

export enum SocialMediaProviders {
    FACEBOOK = 'facebook',
    INSTAGRAM = 'instagram',
    TWITTER = 'twitter',
}

export enum AnnouncementTemplates {
    INSTAGRAM = 'instagram',
    TWITTER = 'twitter',
    FACEBOOK = 'facebook',
    CONTENT = 'content',
    VIDEO = 'video',
    YOUTUBE = 'youtube',
    VIMEO = 'vimeo',
    InsufficientPlaylistRights = 'insufficient-playlist-rights',
    VideoNotSupported = 'video-not-supported',
    Default = 'default',
}

export enum Animation {
    Fade = 'fade',
    SlideRight = 'slide-right',
    SlideLeft = 'slide-left',
    SlideUp = 'slide-up',
    SlideDown = 'slide-down',
    None = '',
}

export const DaysOfWeekValues = Object.values(AbbreviatedDaysOfWeek).map((dow) => String(dow));

const AnnouncementShapeConfigurationSchema = z.object({
    /**
     * PK ID from database
     */
    id: z.union([z.string(), z.number()]).nullish().transform(zMarshalToStrictIntNullable),
    /**
     * shape width in "<width>px" notation where <width> is the numeric width in pixels.
     */
    width: z.string(),
    /**
     * shape height in "<height>px" notation where <height> is the numeric height in pixels.
     */
    height: z.string(),
    /**
     * x offset of the shape on the canvas in pixel notation
     */
    x: z.string(),
    /**
     * y offset of the shape on the canvas in pixel notation
     */
    y: z.string(),
    /**
     * Z-index of the shape on the canvas
     */
    z: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
    /**
     * visual animation applied to the shape after fade in
     */
    animation: z.nativeEnum(Animation).nullable(),
    /**
     * geometry of the shape
     */
    type: z.enum(['rect', 'ellipse', 'rectangle']),
    /**
     * HTML hex code indicating shape color.
     */
    color: z.string(),
});
export type AnnouncementShapeConfiguration = z.infer<typeof AnnouncementShapeConfigurationSchema>;

const AnnouncementTextboxConfigurationSchema = z.object({
    /**
     * PK ID from database
     */
    id: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
    /**
     * text content for the text box
     */
    text: z.string().nullable(),
    /**
     * font to use for the text
     */
    font: z.string(),
    /**
     * font alignment in the textbox region
     */
    align: z.enum(['', 'center', 'left', 'right']),
    /**
     * font size in pixel notation
     */
    size: z.string(),
    /**
     * CSS line height in pixel notation
     */
    lineheight: z.string(),
    /**
     * HTML hex code indicating text color.
     */
    color: z.string(),
    /**
     * CSS font weight
     */
    bold: z.string(), // z.enum(['400', '700', 'normal', 'bold', '']),
    /**
     * if text is italicized
     */
    italic: z.enum(['italic', 'normal']).default('normal'),
    /**
     * either none, underline, or rgb() notation
     */
    underline: z.string(),
    /**
     * if drop shadow should be applied
     */
    shadow: z
        .enum(['false', 'true', 'undefined', ''])
        .default('false')
        .refine((value) => (value === 'true' ? 'true' : 'false'))
        .transform(zMarshalToBoolean),
    /**
     * x offset of the textbox on the canvas in pixel notation
     */
    x: z.string(),
    /**
     * y offset of the textbox on the canvas in pixel notation
     */
    y: z.string(),
    /**
     * Z-index of the textbox on the canvas
     */
    z: z.literal('').or(z.union([z.string(), z.number()]).transform(zMarshalToStrictInt)),
    /**
     * visual animation applied to the textbox after fade in
     */
    animation: z.nativeEnum(Animation).nullable(),
    /**
     * textbox width in "<width>px" notation where <width> is the numeric width in pixels.
     */
    width: z.string(),
    /**
     * textbox height in "<height>px" notation where <height> is the numeric height in pixels.
     */
    height: z.string(),
});
export type AnnouncementTextboxConfiguration = z.infer<
    typeof AnnouncementTextboxConfigurationSchema
>;

const AnnouncementImageConfigurationSchema = z.object({
    /**
     * PK ID from database
     */
    id: z.union([z.string(), z.number()]).nullish().transform(zMarshalToStrictIntNullable),
    /**
     * image width in "<width>px" notation where <width> is the numeric width in pixels.
     */
    width: z.string(),
    /**
     * image height in "<height>px" notation where <height> is the numeric height in pixels.
     */
    height: z.string(),
    /**
     * x offset of the image on the canvas in pixel notation
     */
    x: z.string(),
    /**
     * y offset of the image on the canvas in pixel notation
     */
    y: z.string(),
    /**
     * Z-index of the image on the canvas. Observed values:
     *  '', 'auto', <integer>
     */
    z: z.string(),
    /**
     * visual animation applied to the image after fade in
     */
    animation: z.nativeEnum(Animation).nullable(),
    /**
     * image URL
     */
    imgpath: z.string(),
});
export type AnnouncementImageConfiguration = z.infer<typeof AnnouncementImageConfigurationSchema>;

const AnnouncementBaseConfigurationSchema = z
    .object({
        /**
         * ID of the announcement. PK in the database
         */
        id: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
        /**
         * background color of the announcement as HTML hex code.
         */
        background_color: z.string().nullable(),
        /**
         * If the announcement is part of a group, the group id to which it belongs.
         */
        group_id: z
            .union([z.string(), z.number()])
            .nullish()
            .transform(zMarshalToStrictIntNullable),
        /**
         * Order of the announcement.
         */
        order: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
        /**
         * Name of the announcement
         */
        name: z.string(),
        /**
         * Date to start scheduling the announcement for play. See also `stime`.
         * '2000-12-31 00:00:00'
         */
        start: z.string(),
        /**
         * Date to stop scheduling the announcement for play. See also `etime`.
         * '2099-12-31 00:00:00'
         */
        end: z.string(),
        /**
         * Status of the announcement. Used to determine if the announcement should be scheduled for
         * play.
         */
        status: z.nativeEnum(AnnouncementStatus),
        /**
         * TODO: look up meaning
         */
        timing: z.number(),
        /**
         * if true, announcement is flagged as an alert.
         */
        alert: z.boolean(),
        /**
         * TODO: look up meaning
         */
        updated: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
        /**
         * An identifier for determining the type of announcement
         * TODO: Use an enum
         */
        template: z.string(),
        /**
         * PK media library item. Only populated if the announcement is from media library
         * and not from legacy. This is in base properties because it is used by proof of play
         * reporting which exists in AnnouncementBase class.
         */
        media_library_item_id: z
            .union([z.string(), z.number()])
            .nullish()
            .transform(zMarshalToNumberNullable),
        /**
         * determines if the announcement should be fullscreen or not
         */
        go_full_screen: z.any(),
        /**
         * width of the announcement in pixels. Used by various formats
         */
        width: z.union([z.string(), z.number()]).transform(zMarshalToNumber),
        /**
         * height of the announcement in pixels. Used by various formats
         */
        height: z.union([z.string(), z.number()]).transform(zMarshalToNumber),
    })
    .passthrough();

export type AnnouncementBaseConfiguration = z.infer<typeof AnnouncementBaseConfigurationSchema>;

const ContentAnnouncementConfigurationSchema = AnnouncementBaseConfigurationSchema.extend({
    template: z.literal('content'),
    /**
     * The category of content:
     *  news
     *  sports
     *  ent (entertainment)
     */
    content_type: z.string().nullable(), //z.nullable(z.enum(['news', 'sports', 'ent'])),
});

export type ContentAnnouncementConfiguration = z.infer<
    typeof ContentAnnouncementConfigurationSchema
>;

const RegularAnnouncementConfigurationSchema = AnnouncementBaseConfigurationSchema.extend({
    template: z.literal('default'),
    /**
     * images created within the announcement
     */
    images: z.array(AnnouncementImageConfigurationSchema).default([]),
    /**
     * shapes created within the announcement
     */
    shapes: z.array(AnnouncementShapeConfigurationSchema).default([]),
    /**
     * textboxes created with the announcement
     */
    textboxes: z.array(AnnouncementTextboxConfigurationSchema).default([]),
    /**
     * URL to a "background image" for the announcement
     */
    bgimg: z.string(),
});

export type RegularAnnouncementConfiguration = z.infer<
    typeof RegularAnnouncementConfigurationSchema
>;

const VideoNotSupportedAnnouncementConfigurationSchema = AnnouncementBaseConfigurationSchema.extend(
    {
        template: z.literal('video-not-supported'),
    }
);

export type VideoNotSupportedAnnouncementConfiguration = z.infer<
    typeof VideoNotSupportedAnnouncementConfigurationSchema
>;

const SocialMediaBaseConfigurationSchema = AnnouncementBaseConfigurationSchema.extend({
    /**
     * In social media, each provider has 2-3 templates called
     * "a", "b", or "c".
     */
    sm_template: z.enum(['a', 'b', 'c']).nullable(),
    /**
     * vendor-specific social media account id
     */
    sm_id: z.string().nullable(),
    /**
     * REACH's internal social media id
     */
    reach_sm_id: z.string().nullable(),
});

const InstagramAnnouncementConfigurationSchema = SocialMediaBaseConfigurationSchema.extend({
    template: z.literal('instagram'),
});

export type InstagramAnnouncementConfiguration = z.infer<
    typeof RegularAnnouncementConfigurationSchema
>;

const TwitterAnnouncementConfigurationSchema = SocialMediaBaseConfigurationSchema.extend({
    template: z.literal('twitter'),
});

export type TwitterAnnouncementConfiguration = z.infer<
    typeof RegularAnnouncementConfigurationSchema
>;

const FacebookAnnouncementConfigurationSchema = SocialMediaBaseConfigurationSchema.extend({
    template: z.literal('facebook'),
});

export type FacebookAnnouncementConfiguration = z.infer<
    typeof RegularAnnouncementConfigurationSchema
>;

const VideoBaseAnnouncementConfigurationSchema = AnnouncementBaseConfigurationSchema.extend({
    /**
     * if true and if announcement is a youtube video, enables closed captioning, if avaialble
     * Used by both youtube and Vimeo
     * TODO: rename to "enable_cc"
     */
    youtube_enable_cc: z.unknown().transform(zMarshalToBoolean),
    /**
     * second offset to start playing the video at
     */
    video_start_time: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
    /**
     * TODO: what does this actually mean? Does duration take into account start time?
     */
    video_total_duration: z.union([z.string(), z.number()]).transform(zMarshalToNumber),
});

export type VideoBaseAnnouncementConfiguration = z.infer<
    typeof VideoBaseAnnouncementConfigurationSchema
>;

const ReachVideoAnnouncementConfigurationSchema = VideoBaseAnnouncementConfigurationSchema.extend({
    template: z.literal('video'),
    video_id: z.string(),
});

export type ReachVideoAnnouncementConfiguration = z.infer<
    typeof ReachVideoAnnouncementConfigurationSchema
>;

const YoutubeAnnouncementConfigurationSchema = VideoBaseAnnouncementConfigurationSchema.extend({
    template: z.literal('youtube'),
    /**
     * link to the youtube video
     */
    youtubelink: z.string().nullable(),
});

export type YoutubeAnnouncementConfiguration = z.infer<
    typeof YoutubeAnnouncementConfigurationSchema
>;

const VimeoAnnouncementConfigurationSchema = VideoBaseAnnouncementConfigurationSchema.extend({
    template: z.literal('vimeo'),
    /**
     * Vimeo ID of the video
     */
    vimeo_video_id: z.string(),
});

export type VimeoAnnouncementConfiguration = z.infer<typeof VimeoAnnouncementConfigurationSchema>;

const VideoAnnouncementConfigurationSchema = z.union([
    ReachVideoAnnouncementConfigurationSchema,
    YoutubeAnnouncementConfigurationSchema,
    VimeoAnnouncementConfigurationSchema,
]);
export type VideoAnnouncementConfiguration = z.infer<typeof VideoAnnouncementConfigurationSchema>;

const SocialMediaAnnouncementConfigurationSchema = z.union([
    TwitterAnnouncementConfigurationSchema,
    FacebookAnnouncementConfigurationSchema,
    InstagramAnnouncementConfigurationSchema,
]);
export type SocialMediaAnnouncementConfiguration = z.infer<
    typeof SocialMediaAnnouncementConfigurationSchema
>;

const InsufficientPlaylistRightsAnnouncementConfigurationSchema =
    AnnouncementBaseConfigurationSchema.extend({
        template: z.literal('insufficient-playlist-rights'),
    });
export type InsufficientPlaylistRightsAnnouncementConfiguration = z.infer<
    typeof InsufficientPlaylistRightsAnnouncementConfigurationSchema
>;

const DefaultAnnouncementConfigurationSchema = z.union([
    RegularAnnouncementConfigurationSchema,
    VideoNotSupportedAnnouncementConfigurationSchema,
    InsufficientPlaylistRightsAnnouncementConfigurationSchema,
]);
export type DefaultAnnouncementConfiguration = z.infer<
    typeof DefaultAnnouncementConfigurationSchema
>;
